/**
 * BrandingProvider — runtime tenant theming for the admin portal.
 *
 * Lifted from the edu-blend pattern (F9 §10.4):
 * - On mount, resolves the current hostname to a tenant via the public
 *   `/tenants/resolve` endpoint (no auth needed).
 * - Maps `colorTokens` JSONB → CSS custom properties on `document.documentElement`.
 * - Swaps the `<link rel="icon">` with the tenant's favicon.
 * - Injects sanitized `customCss` into a managed `<style>` tag.
 * - Falls back to the build-time house brand (`activeBrand`, selected via
 *   `VITE_BRAND` — see `brands/` and `BRANDING.md`) when the host doesn't
 *   resolve to a tenant, including that brand's baked `tenantId` so the build
 *   keeps tenant scope on a host with no `TenantDomain` row.
 */

import {
  createContext,
  useContext,
  useEffect,
  type ReactNode,
} from 'react';
import { useAppDispatch } from '@/store/hooks';
import {
  setEnabledProducts,
  setSelectedTenantId,
  setSelectedTenantIdIfUnset,
} from '@/features/tenants/tenant-slice';
import { useTenantResolve } from '@/hooks/use-tenant-resolve';
import type {
  TenantBranding,
  ResolveResponse,
  TenantResolution,
} from '@/features/platform/api';
import { activeBrand, activeTenantId } from '@brands/active';
import { brandCssVars } from '@brands/color';

/**
 * House brand baked into this build. Selected at build time by `VITE_BRAND`
 * (default: Nuur). This is the runtime fallback applied when `/tenants/resolve`
 * returns nothing for the current host. A resolved tenant's branding still
 * shadows it automatically (see the `?? HOUSE_BRANDING` wiring in the provider).
 */
const HOUSE_BRANDING: TenantBranding = activeBrand.branding;

/**
 * Fill a tenant row's empty fields from the house brand — only ever called for
 * the tenant this build is baked for, whose assets live in `brands/`. Without
 * it a half-filled row (a `TenantBranding` with no logo, the common case) blew
 * away the baked mark and `logoType`, and the lockup lost its name.
 */
function withHouseFallback(row: TenantBranding | null): TenantBranding {
  if (!row) return HOUSE_BRANDING;
  const supplied = Object.fromEntries(
    Object.entries(row).filter(([, v]) => v !== null && v !== undefined && v !== '')
  ) as Partial<TenantBranding>;
  return {
    ...HOUSE_BRANDING,
    ...supplied,
    colorTokens: { ...HOUSE_BRANDING.colorTokens, ...(row.colorTokens ?? {}) },
  };
}

/* ─── Context shape ──────────────────────────────────────────────────── */

interface BrandingContextValue {
  /**
   * Tenant UUID in effect — the host-resolved tenant, else the tenant baked
   * into this build (`activeTenantId`). Null only for a brand-only build whose
   * host doesn't resolve.
   */
  tenantId: string | null;
  /** Tenant display name (falls back to "Mazeed Pay"). */
  displayName: string;
  /** Full branding object (null = house brand defaults). */
  branding: TenantBranding | null;
  /**
   * Product codes enabled for this tenant, or `null` when the host resolved to
   * no tenant — "unknown", not "none". Gates product-scoped features; see
   * `lib/auth/product-scopes`.
   */
  enabledProducts: string[] | null;
  /** True while the resolve call is in-flight. */
  isLoading: boolean;
  /**
   * Defaults to `unavailable`, never `unmapped`, while the query hasn't
   * settled — never guess a lockout.
   */
  resolution: TenantResolution;
}

const BrandingContext = createContext<BrandingContextValue>({
  tenantId: activeTenantId,
  displayName: HOUSE_BRANDING.displayName,
  branding: null,
  enabledProducts: null,
  isLoading: true,
  resolution: { status: 'unavailable' },
});

export const useBranding = () => useContext(BrandingContext);

/* ─── CSS variable injection ─────────────────────────────────────────── */

function applyBrandingToDOM(branding: TenantBranding | null): () => void {
  const root = document.documentElement;
  const cleanups: (() => void)[] = [];

  if (!branding) return () => {};

  // 1. Brand ramp → CSS custom properties. `brandCssVars` emits the full ramp
  //    in BOTH hex (`--mz-brand-*`) and HSL-channel (`--mz-brand-*-hsl`) form
  //    plus `--mz-brand-hue`, so the Tailwind `brand-*` scale AND every
  //    semantic token (index.css) re-theme from this single source. A tenant
  //    that supplies only `primary` gets a full derived ramp.
  const vars = brandCssVars(branding);
  for (const [cssVar, value] of Object.entries(vars)) {
    const prev = root.style.getPropertyValue(cssVar);
    root.style.setProperty(cssVar, value);
    cleanups.push(() => {
      if (prev) root.style.setProperty(cssVar, prev);
      else root.style.removeProperty(cssVar);
    });
  }

  // 2. Favicon swap
  if (branding.faviconUrl) {
    const existing = document.querySelector<HTMLLinkElement>(
      'link[rel="icon"]'
    );
    const prevHref = existing?.href;
    if (existing) {
      existing.href = branding.faviconUrl;
      cleanups.push(() => {
        existing.href = prevHref || '/favicon.ico';
      });
    } else {
      const link = document.createElement('link');
      link.rel = 'icon';
      link.href = branding.faviconUrl;
      document.head.appendChild(link);
      cleanups.push(() => link.remove());
    }
  }

  // 3. Custom CSS (sanitized server-side per F9 §10.4)
  if (branding.customCss) {
    const style = document.createElement('style');
    style.setAttribute('data-tenant-css', 'true');
    style.textContent = branding.customCss;
    document.head.appendChild(style);
    cleanups.push(() => style.remove());
  }

  // 4. Font family
  if (branding.fontFamily) {
    const prev = root.style.fontFamily;
    root.style.fontFamily = branding.fontFamily;
    cleanups.push(() => {
      root.style.fontFamily = prev;
    });
  }

  return () => cleanups.forEach((c) => c());
}

/* ─── Document title ─────────────────────────────────────────────────── */

function updateDocumentTitle(displayName: string, isUnmapped: boolean) {
  // Naming a tenant here would be a guess the body already refuses to make.
  // Neutral literal, not i18n — this provider sits outside the i18n tree.
  document.title = isUnmapped
    ? 'Admin Portal'
    : `${displayName} — Admin Portal`;
}

/* ─── Provider ───────────────────────────────────────────────────────── */

export function BrandingProvider({ children }: { children: ReactNode }) {
  const dispatch = useAppDispatch();
  const { data, isLoading } = useTenantResolve();

  const resolution: TenantResolution = data ?? { status: 'unavailable' };
  const resolved: ResolveResponse | null =
    resolution.status === 'resolved' ? resolution.tenant : null;
  const resolvedTenantId = resolved?.tenantId ?? null;
  // A branded build carries its tenant, so an unmapped host still has scope
  // instead of a null tenant. A resolved host always wins.
  const tenantId = resolvedTenantId ?? activeTenantId;
  // When the resolve call comes back empty (no TenantDomain mapping for
  // this hostname), fall back to the build-time house brand. Switching the
  // house brand is a build option (`VITE_BRAND`) — no code change here.
  const displayName = resolved?.displayName ?? HOUSE_BRANDING.displayName;
  // The house brand fills the gaps only for its own tenant — for anyone else
  // it would dress their portal in this build's logo.
  const isHouseTenant =
    resolvedTenantId !== null && resolvedTenantId === activeTenantId;
  const branding = resolved
    ? isHouseTenant
      ? withHouseFallback(resolved.branding)
      : (resolved.branding ?? HOUSE_BRANDING)
    : HOUSE_BRANDING;
  const enabledProducts = resolved?.enabledProducts ?? null;

  // Apply branding to the DOM when resolved data changes.
  useEffect(() => {
    const cleanup = applyBrandingToDOM(branding);
    updateDocumentTitle(displayName, resolution.status === 'unmapped');
    return cleanup;
  }, [branding, displayName, resolution.status]);

  // Auto-set the tenant in Redux store for non-Super Admin users.
  //
  // Waits for the resolve to settle so the baked fallback can't briefly stamp
  // the wrong `X-Tenant-Id` on in-flight requests before the host's own tenant
  // arrives. `selectedTenantId` is deliberately NOT read here — subscribing to
  // it would re-fire this effect on every Super Admin tenant switch and snap
  // the selection back; the "only if unset" test lives in the reducer instead.
  useEffect(() => {
    if (isLoading) return;
    if (resolvedTenantId) {
      dispatch(setSelectedTenantId(resolvedTenantId));
      return;
    }
    if (activeTenantId) {
      dispatch(setSelectedTenantIdIfUnset(activeTenantId));
    }
  }, [isLoading, resolvedTenantId, dispatch]);

  // Mirror the entitlements into Redux for the one consumer that cannot read
  // this context: the `_layout` route guard, which runs in `beforeLoad`
  // outside React. Published only once the resolve settles, so the guard sees
  // `null` (unknown, open) rather than a premature "no products".
  useEffect(() => {
    if (isLoading) return;
    dispatch(setEnabledProducts(enabledProducts));
  }, [isLoading, enabledProducts, dispatch]);

  return (
    <BrandingContext.Provider
      value={{
        tenantId,
        displayName,
        branding,
        enabledProducts,
        isLoading,
        resolution,
      }}
    >
      {children}
    </BrandingContext.Provider>
  );
}

export default BrandingProvider;
